import { FmdBadRounded } from '@mui/icons-material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import HeightIcon from '@mui/icons-material/Height';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import NavigationIcon from '@mui/icons-material/Navigation';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import PlaceIcon from '@mui/icons-material/Place';
import SpeedIcon from '@mui/icons-material/Speed';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { AUTHORITIES } from 'app/config/constants';
import { REACT_APP_MAP_KEY } from 'app/config/map-key';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { mapStyle } from 'app/shared/layout/maps/CustomStyle';
import { ICameraEventMedia } from 'app/shared/model/camera-event-media.model';
import { ICameraEvent } from 'app/shared/model/camera-event.model';
import { getAddress } from 'app/shared/reducers/geocode.reducer';
import { convertDateTimeToPtBRWithYear } from 'app/shared/util/date-utils';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, OverlayView, withGoogleMap, withScriptjs } from 'react-google-maps';
import { translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { compose, withProps } from 'recompose';
import { getMedia } from './camera-event.reducer';

interface Preview {
  url: string;
  contentType: string;
  title: string;
}

const MEDIA_RETENTION_DAYS = 30;
const MEDIA_RETENTION_MILLISECONDS = MEDIA_RETENTION_DAYS * 24 * 60 * 60 * 1000;

const formatBytes = (value?: number | null) => {
  if (value == null) return '-';
  if (value === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatSpeed = (value?: number | null) => (value == null ? '-' : `${value} km/h`);

const isValidCoordinate = (latitude?: number | null, longitude?: number | null) => latitude != null && longitude != null && latitude !== 0 && longitude !== 0;

const formatNumber = (value?: number | null) => (value == null ? '-' : value);
const normalizeMediaType = (value?: string | null) => (value || 'file').toLowerCase();
const isVideoMedia = (value?: string | null) => normalizeMediaType(value).includes('video');
const isImagePreview = (value?: string | null) => value?.startsWith('image/') || normalizeMediaType(value).includes('image');
const isVideoPreview = (value?: string | null) =>
  value?.startsWith('video/') || value === 'application/x-mpegURL' || value === 'application/vnd.apple.mpegurl' || isVideoMedia(value);
const mediaTypeLabel = (value?: string | null) => {
  const type = normalizeMediaType(value);
  if (type.includes('image')) return translate('fleetSenseApp.cameraEvent.media.type.image');
  if (type.includes('video')) return translate('fleetSenseApp.cameraEvent.media.type.video');
  if (type.includes('audio')) return translate('fleetSenseApp.cameraEvent.media.type.audio');
  return translate('fleetSenseApp.cameraEvent.media.type.other');
};

const mediaContentType = (item: ICameraEventMedia, responseContentType?: string) => {
  if (responseContentType && responseContentType !== 'application/octet-stream') return responseContentType;
  if (isVideoMedia(item.mediaType)) return 'video/mp4';
  if (normalizeMediaType(item.mediaType).includes('image')) return 'image/jpeg';
  return responseContentType || 'application/octet-stream';
};

const mediaFileNameDatePattern = /alarm_(\d{14})/;

const getMediaCreatedAt = (fileName?: string | null) => {
  const match = fileName ? mediaFileNameDatePattern.exec(fileName) : null;
  if (!match) return null;

  const [, value] = match;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  const hour = Number(value.slice(8, 10));
  const minute = Number(value.slice(10, 12));
  const second = Number(value.slice(12, 14));
  const createdAt = new Date(year, month, day, hour, minute, second);

  return Number.isNaN(createdAt.getTime()) ? null : createdAt;
};

const getMediaExpiresAt = (fileName?: string | null) => {
  const createdAt = getMediaCreatedAt(fileName);
  return createdAt ? new Date(createdAt.getTime() + MEDIA_RETENTION_MILLISECONDS) : null;
};

const formatExpirationRemainingDays = (expiresAt: Date) => {
  const remainingMilliseconds = expiresAt.getTime() - Date.now();
  if (remainingMilliseconds <= 0) return 0;

  return Math.max(Math.floor(remainingMilliseconds / (24 * 60 * 60 * 1000)) - 1, 0);
};

const alarmCategoryLabel = (alarmClass?: string | null) => {
  switch (alarmClass) {
    case 'badge-adas':
      return 'ADAS · Colisão/Faixa';
    case 'badge-dsm':
      return 'DSM · Fadiga/Celular';
    case 'badge-harsh':
      return 'Condução Agressiva';
    case 'badge-system':
      return 'Sistema/Hardware';
    default:
      return '-';
  }
};

const alarmCategoryShortLabel = (alarmClass?: string | null) => {
  switch (alarmClass) {
    case 'badge-adas':
      return 'Colisão/Faixa';
    case 'badge-dsm':
      return 'Fadiga/Celular';
    case 'badge-harsh':
      return 'Condução Agressiva';
    case 'badge-system':
      return 'Sistema/Hardware';
    default:
      return '';
  }
};

const alarmChipColor = (alarmClass?: string | null) => {
  switch (alarmClass) {
    case 'badge-adas':
      return 'info';
    case 'badge-dsm':
      return 'warning';
    case 'badge-harsh':
      return 'error';
    case 'badge-system':
      return 'default';
    default:
      return 'default';
  }
};

const alarmMarkerColor = (alarmClass: string | null | undefined, theme: Theme) => {
  switch (alarmClass) {
    case 'badge-adas':
      return theme.palette.info.main;
    case 'badge-dsm':
      return theme.palette.warning.main;
    case 'badge-harsh':
      return theme.palette.error.main;
    case 'badge-system':
      return theme.palette.text.secondary;
    default:
      return theme.palette.info.main;
  }
};

const MediaTypeIcon = ({ mediaType }: { mediaType?: string | null }) => {
  const type = normalizeMediaType(mediaType);
  if (type.includes('image')) {
    return <PhotoOutlinedIcon fontSize="small" color="secondary" />;
  }
  if (type.includes('video')) {
    return <VideocamOutlinedIcon fontSize="small" color="secondary" />;
  }
  return <InsertDriveFileOutlinedIcon fontSize="small" color="secondary" />;
};

const eventMapApiUrl = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAP_KEY}&v=3.exp&libraries=geometry,drawing,places`;

const EventLocationMap = compose(
  withProps({
    googleMapURL: eventMapApiUrl,
    loadingElement: (
      <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress color="secondary" size={28} />
      </Box>
    ),
    containerElement: <Box sx={{ height: '100%', width: '100%' }} />,
    mapElement: <Box sx={{ height: '100%', width: '100%' }} />,
  }),
  withScriptjs,
  withGoogleMap
)(({ center, markerTitle, markerColor }: { center: { lat: number; lng: number }; markerTitle: string; markerColor: string }) => (
  <GoogleMap defaultZoom={16} defaultCenter={center} defaultOptions={{ styles: mapStyle }}>
    <OverlayView
      position={center}
      mapPaneName={(OverlayView as any).OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(width: number, height: number) => ({
        x: -width / 2,
        y: -height / 2,
      })}
    >
      <Box
        title={markerTitle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: markerColor,
          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.28))',
        }}
      >
        <FmdBadRounded sx={{ fontSize: 44 }} />
      </Box>
    </OverlayView>
  </GoogleMap>
));

type DetailParams = { hatDeviceCode: string; hatEventId: string };

export const CameraEventDetail = (props: RouteComponentProps<DetailParams>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const hatDeviceCode = decodeURIComponent(props.match.params.hatDeviceCode);
  const hatEventId = decodeURIComponent(props.match.params.hatEventId);

  const navigationState = props.location.state as { event?: ICameraEvent; returnTo?: string } | undefined;
  const event = navigationState?.event ?? ({} as ICameraEvent);
  const returnTo = navigationState?.returnTo || '/camera-events';

  const media = useAppSelector(state => state.cameraEvent.media);
  const mediaLoading = useAppSelector(state => state.cameraEvent.mediaLoading);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const [preview, setPreview] = useState<Preview | null>(null);
  const [contentLoadingName, setContentLoadingName] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [eventAddress, setEventAddress] = useState<string>('');
  const [addressLoading, setAddressLoading] = useState(false);
  const contentRequestIdRef = useRef(0);

  useEffect(() => {
    contentRequestIdRef.current += 1;
    setPreview(null);
    setContentLoadingName(null);
    setContentError(null);
    dispatch(getMedia({ hatDeviceCode, hatEventId }));
  }, [dispatch, hatDeviceCode, hatEventId]);

  useEffect(
    () => () => {
      if (preview?.url.startsWith('blob:')) {
        URL.revokeObjectURL(preview.url);
      }
    },
    [preview]
  );

  const getContent = async (item: ICameraEventMedia, download = false) => {
    if (!item.fileName) return;

    const requestId = ++contentRequestIdRef.current;
    setContentError(null);
    setContentLoadingName(item.fileName);
    try {
      const url = `api/camera-events/${encodeURIComponent(hatDeviceCode)}/${encodeURIComponent(hatEventId)}/media/content?fileName=${encodeURIComponent(
        item.fileName
      )}`;

      const response = await axios.get<Blob>(url, { responseType: 'blob' });
      if (requestId !== contentRequestIdRef.current) return;

      const contentType = mediaContentType(item, response.headers['content-type']);
      if (download) {
        const objectUrl = URL.createObjectURL(new Blob([response.data], { type: contentType }));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = item.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } else {
        const objectUrl = URL.createObjectURL(new Blob([response.data], { type: contentType }));
        setPreview({ url: objectUrl, contentType, title: item.fileName });
      }
    } catch (_error) {
      if (requestId === contentRequestIdRef.current) {
        setContentError(translate('fleetSenseApp.cameraEvent.media.contentUnavailable'));
      }
    } finally {
      if (requestId === contentRequestIdRef.current) setContentLoadingName(null);
    }
  };

  const closePreview = () => {
    if (preview?.url.startsWith('blob:')) {
      URL.revokeObjectURL(preview.url);
    }
    setPreview(null);
  };
  const goBack = () => props.history.push(returnTo);

  const expectedMediaCount = event.mediaCount ?? event.attachCount ?? 0;
  const completedMediaCount = media.length;
  const earliestMediaExpiration = media
    .filter(item => item.contentAvailable)
    .map(item => getMediaExpiresAt(item.fileName))
    .filter((expiresAt): expiresAt is Date => Boolean(expiresAt))
    .sort((first, second) => first.getTime() - second.getTime())[0];
  const mediaExpirationRemainingDays = earliestMediaExpiration ? formatExpirationRemainingDays(earliestMediaExpiration) : null;
  const hasLocation = isValidCoordinate(event.latitude, event.longitude);
  const eventLocation = hasLocation ? { lat: Number(event.latitude), lng: Number(event.longitude) } : null;
  const locationValue = hasLocation
    ? addressLoading
      ? translate('fleetSenseApp.cameraEvent.detail.addressLoading')
      : eventAddress || translate('fleetSenseApp.cameraEvent.detail.addressNotFound')
    : translate('fleetSenseApp.cameraEvent.withoutGps');

  useEffect(() => {
    if (!hasLocation || event.latitude == null || event.longitude == null) {
      setEventAddress('');
      return;
    }

    setAddressLoading(true);
    getAddress(event.latitude, event.longitude)
      .then(response => {
        setEventAddress(response.data?.address || '');
      })
      .catch(() => {
        setEventAddress('');
      })
      .finally(() => {
        setAddressLoading(false);
      });
  }, [hasLocation, event.latitude, event.longitude]);

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', padding: '24px' }}>
      <PageHeader
        headingId="camera-event-detail-heading"
        headingDataCy="CameraEventDetailHeading"
        contentKey="fleetSenseApp.cameraEvent.detail.title"
        showButton={false}
        onBackClick={goBack}
      />

      <Grid container spacing={2.5} alignItems="stretch">
        <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
          <DetailSection contentSx={{ width: '100%', height: '100%' }}>
            <Stack spacing={1.4}>
              <Box>
                <Typography color="text.secondary" fontSize="0.8125rem">
                  {translate('fleetSenseApp.cameraEvent.alarmName')}
                </Typography>
                <Chip
                  color={alarmChipColor(event.alarmClass) as any}
                  label={event.alarmName || '-'}
                  size="small"
                  variant="outlined"
                  sx={{
                    mt: 0.5,
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    '& .MuiChip-label': {
                      display: 'block',
                      whiteSpace: 'normal',
                      py: 0.4,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      lineHeight: 1.25,
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: isAdmin ? '1fr 1fr' : '1fr' },
                  gap: 1.4,
                }}
              >
                <DetailMetric label="fleetSenseApp.cameraEvent.alarmClass" value={alarmCategoryLabel(event.alarmClass)} />
                {isAdmin && <DetailMetric label="fleetSenseApp.cameraEvent.company" value={event.companyName} />}
                <DetailMetric label="fleetSenseApp.cameraEvent.truckImplement" value={event.truckImplementLicensePlate} />
                <DetailMetric label="fleetSenseApp.cameraEvent.occurredAt" value={convertDateTimeToPtBRWithYear(event.occurredAt)} />
              </Box>
            </Stack>

            <Divider sx={{ my: 1.6 }} />

            <Typography color="text.primary" fontWeight="700" fontSize="0.9375rem" sx={{ mb: 1 }}>
              {translate('fleetSenseApp.cameraEvent.detail.telemetry')}
            </Typography>
            <TelemetryItems event={event} />
          </DetailSection>
        </Grid>

        <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
          <DetailSection
            title={translate('fleetSenseApp.cameraEvent.media.title')}
            contentSx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {contentError && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                {contentError}
              </Alert>
            )}
            {!mediaLoading && mediaExpirationRemainingDays !== null && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                {translate('fleetSenseApp.cameraEvent.media.expiration.availableUntilPrefix')}{' '}
                <Typography component="span" fontSize="0.8rem" fontWeight="800">
                  {translate('fleetSenseApp.cameraEvent.media.expiration.remainingDays', { count: mediaExpirationRemainingDays })}
                </Typography>
                {translate('fleetSenseApp.cameraEvent.media.expiration.availableUntilSuffix')}
              </Alert>
            )}
            {!mediaLoading && expectedMediaCount > 0 && completedMediaCount < expectedMediaCount && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                {translate('fleetSenseApp.cameraEvent.media.pending', { completed: completedMediaCount, expected: expectedMediaCount })}
              </Alert>
            )}
            {mediaLoading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress color="secondary" size={28} />
              </Box>
            )}
            {!mediaLoading && !media.length && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  borderStyle: 'dashed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  minHeight: 84,
                  backgroundColor: 'background.default',
                }}
              >
                <PermMediaIcon sx={{ fontSize: 30, color: 'text.secondary', opacity: 0.45, flexShrink: 0 }} />
                <Typography color="text.secondary" fontSize="0.875rem">
                  {translate('fleetSenseApp.cameraEvent.media.notFound')}
                </Typography>
              </Paper>
            )}
            <Grid container spacing={1.5}>
              {media.map(item => (
                <Grid item xs={12} md={6} key={item.fileName}>
                  <Paper
                    variant="outlined"
                    onClick={() => {
                      if (item.contentAvailable && item.fileName && contentLoadingName !== item.fileName) {
                        getContent(item);
                      }
                    }}
                    sx={{
                      p: 1.5,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      minHeight: 76,
                      backgroundColor: 'background.default',
                      borderColor: 'rgba(128, 128, 128, 0.22)',
                      cursor: item.contentAvailable && item.fileName ? 'pointer' : 'default',
                      transition: 'border-color 0.2s, background-color 0.2s',
                      '&:hover': {
                        borderColor: item.contentAvailable && item.fileName ? 'secondary.main' : 'rgba(128, 128, 128, 0.22)',
                        backgroundColor: 'background.paper',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        backgroundColor: 'action.hover',
                        flexShrink: 0,
                      }}
                    >
                      <MediaTypeIcon mediaType={item.mediaType} />
                    </Avatar>
                    <Box minWidth={0} flex={1}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                        <Typography color="text.primary" fontSize="0.8125rem" fontWeight="700" noWrap>
                          {mediaTypeLabel(item.mediaType)}
                        </Typography>
                        <Typography color="text.secondary" fontSize="0.75rem">
                          {formatBytes(item.sizeBytes)}
                        </Typography>
                      </Stack>
                      <Typography color="text.secondary" fontSize="0.75rem" noWrap display="block">
                        {item.contentAvailable
                          ? translate('fleetSenseApp.cameraEvent.media.available')
                          : translate('fleetSenseApp.cameraEvent.media.contentUnavailable')}
                      </Typography>
                    </Box>
                    {item.contentAvailable && item.fileName && (
                      <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                        {contentLoadingName === item.fileName ? (
                          <CircularProgress size={18} color="secondary" />
                        ) : (
                          <>
                            <Tooltip title={translate('entity.action.view')}>
                              <IconButton
                                size="small"
                                onClick={clickEvent => {
                                  clickEvent.stopPropagation();
                                  getContent(item);
                                }}
                              >
                                <VisibilityIcon color="secondary" sx={{ fontSize: 19 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={translate('fleetSenseApp.cameraEvent.media.download')}>
                              <IconButton
                                size="small"
                                onClick={clickEvent => {
                                  clickEvent.stopPropagation();
                                  getContent(item, true);
                                }}
                              >
                                <DownloadIcon color="secondary" sx={{ fontSize: 19 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 3, mb: 1.25 }}>
              <PlaceIcon color="action" sx={{ fontSize: 20, mt: 0.1 }} />
              <Box minWidth={0}>
                <Typography color="text.secondary" fontSize="0.75rem">
                  {translate('fleetSenseApp.cameraEvent.location')}
                </Typography>
                <Typography color="text.primary" fontSize="0.8125rem" fontWeight="600" sx={{ wordBreak: 'break-word' }}>
                  {locationValue}
                </Typography>
              </Box>
            </Stack>

            {eventLocation && (
              <Box sx={{ flex: 1, minHeight: { xs: 280, md: 320 }, borderRadius: '8px', overflow: 'hidden' }}>
                <EventLocationMap
                  center={eventLocation}
                  markerTitle={event.alarmName || translate('fleetSenseApp.cameraEvent.detail.title')}
                  markerColor={alarmMarkerColor(event.alarmClass, theme)}
                />
              </Box>
            )}
          </DetailSection>
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(preview)}
        onClose={closePreview}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            width: 'fit-content',
            maxWidth: 'calc(100vw - 64px)',
            maxHeight: 'calc(100vh - 64px)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1.25, px: 2 }}>
          <Box minWidth={0}>
            <Typography color="text.primary" fontWeight="700" noWrap>
              {event.alarmName || '-'}{' '}
              {alarmCategoryShortLabel(event.alarmClass) && (
                <Typography component="span" color="text.secondary" fontSize="0.8125rem">
                  ({alarmCategoryShortLabel(event.alarmClass)})
                </Typography>
              )}
            </Typography>
          </Box>
          <IconButton aria-label={translate('fleetSenseApp.cameraEvent.media.close')} onClick={closePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'black',
            maxWidth: 'calc(100vw - 64px)',
            maxHeight: 'calc(100vh - 136px)',
          }}
        >
          {preview && isImagePreview(preview.contentType) && (
            <Box
              component="img"
              src={preview.url}
              alt={translate('fleetSenseApp.cameraEvent.media.preview')}
              sx={{
                display: 'block',
                width: 'auto',
                height: 'auto',
                maxWidth: 'calc(100vw - 96px)',
                maxHeight: 'calc(100vh - 168px)',
                objectFit: 'contain',
              }}
            />
          )}
          {preview && isVideoPreview(preview.contentType) && (
            <Box
              component="video"
              src={preview.url}
              controls
              autoPlay
              sx={{
                display: 'block',
                width: 'auto',
                height: 'auto',
                maxWidth: 'calc(100vw - 96px)',
                maxHeight: 'calc(100vh - 168px)',
                objectFit: 'contain',
              }}
            />
          )}
          {preview && !isImagePreview(preview.contentType) && !isVideoPreview(preview.contentType) && (
            <Typography color="white">{translate('fleetSenseApp.cameraEvent.media.previewUnsupported')}</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const DetailSection = ({ title, children, contentSx = {} }: { title?: string; children: React.ReactNode; contentSx?: Record<string, unknown> }) => (
  <Box sx={{ backgroundColor: 'background.paper', borderRadius: '8px', p: 2.25, ...contentSx }}>
    {title && (
      <Typography color="text.primary" fontWeight="700" fontSize="0.9375rem" sx={{ mb: 2 }}>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

const TelemetryItems = ({ event }: { event: ICameraEvent }) => (
  <Stack spacing={0.5}>
    <TelemetryRow icon={<SpeedIcon fontSize="small" color="action" />} label="fleetSenseApp.cameraEvent.speed" value={formatSpeed(event.speed)} />
    <TelemetryRow icon={<AltRouteIcon fontSize="small" color="action" />} label="fleetSenseApp.cameraEvent.mileage" value={formatNumber(event.mileage)} />
    <TelemetryRow icon={<NavigationIcon fontSize="small" color="action" />} label="fleetSenseApp.cameraEvent.direction" value={formatNumber(event.direction)} />
    <TelemetryRow icon={<HeightIcon fontSize="small" color="action" />} label="fleetSenseApp.cameraEvent.altitude" value={formatNumber(event.altitude)} last />
  </Stack>
);

const DetailMetric = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
  <Box>
    <Typography color="text.secondary" fontSize="0.8125rem">
      {translate(label)}
    </Typography>
    <Typography color="text.primary" fontSize="0.8125rem" fontWeight="600" sx={{ wordBreak: 'break-word' }}>
      {value ?? '-'}
    </Typography>
  </Box>
);

const TelemetryRow = ({
  icon,
  label,
  value,
  last = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number | null | undefined;
  last?: boolean;
}) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.8 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography color="text.secondary" fontSize="0.8125rem">
          {translate(label)}
        </Typography>
      </Stack>
      <Typography color="text.primary" fontSize="0.8125rem" textAlign="right" fontWeight="600" sx={{ wordBreak: 'break-word' }}>
        {value ?? '-'}
      </Typography>
    </Stack>
    {!last && <Divider />}
  </Box>
);

export default CameraEventDetail;
